import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewChecked,
  ApplicationRef,
  EnvironmentInjector,
  signal,
  ElementRef,
  ChangeDetectionStrategy,
  SimpleChanges
} from '@angular/core';
import {Tile, TileId} from '@shared/models/tile';
import {CanvasStructure, CanvasPoint} from '@shared/models/structure';
import {filterByMatchingIds} from '@shared/utils/tile-filter.util';
import {FileUploadService} from '@core/services/file-upload.service';
import {SpinnerComponent} from '@lib/components/spinner/spinner.component';
import {PopoverMenuComponent} from '@lib/components/popover-menu/popover-menu.component';
import {OptionGroup} from '@shared/models/option';
import {TileMenuOptionId} from '@shared/constants/menu-option-ids';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {TilesService} from '@core/services/tiles.service';
import {ShadeToolsService} from '@core/services/shade-tools.service';
import {CanvasTileDrawerComponent} from '../canvas-tile-drawer/canvas-tile-drawer.component';
import {TileEditorComponent} from '@lib/components/tile-editor/tile-editor.component';
import {CanvasReplayControlsComponent} from '../canvas-replay-controls/canvas-replay-controls.component';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import * as L from 'leaflet';
import 'leaflet-gesture-handling';
import {CANVAS_MINI_TILE} from '@features/canvas-tiles/shared/canvas-tile.constants';
import {addCanvasMapControls, CanvasClusterLayer, CANVAS_MAP_CONFIG, createCanvasMap, destroyCanvasMap} from '@features/canvas-tiles/shared/canvas-map-controls';
import {CanvasMarkerRegistry} from '@features/canvas-tiles/shared/canvas-marker-registry';
import {MiniTileClickEvent} from '@features/canvas-tiles/shared/canvas-marker.models';
import {ViewportIntersectionService} from '@core/services/viewport-intersection.service';
import {CanvasReplayService, ReplayTileResult} from '@core/services/canvas-replay.service';
import {FeatureFlagsService, FeatureFlag} from '@core/services/feature-flags.service';

/**
 * Displays a Canvas tile on the dashboard with clickable mini-tiles
 * positioned on the canvas image using Leaflet for zoom/pan capabilities
 */
@Component({
  selector: 'motn-canvas-tile-display',
  standalone: true,
  templateUrl: './canvas-tile-display.component.html',
  styleUrls: ['./canvas-tile-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
    PopoverMenuComponent,
    CanvasTileDrawerComponent,
    TileEditorComponent,
    CanvasReplayControlsComponent,
  ],
})
export class CanvasTileDisplayComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() tile: Tile;

  @Input() matchingTileIds: Set<TileId> | null = null;

  editingTile = false;

  readonly mapId = `canvas-display-map-${this.generateUniqueId()}`;
  canvasStructure: CanvasStructure;
  canvasImageUrl = '';
  loading = signal(true);
  drawerOpen = false;
  drawerTiles: Tile[] = [];
  tileMenuOpen = false;
  readonly tileMenuTitle = 'Tile Features';
  readonly tileMenuOptions = new OptionGroup([{
    id: TileMenuOptionId.CONFIGURE,
    name: 'Configure',
    callback: () => this.editTile(),
  }]);
  readonly popoverPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];

  private map?: L.Map;
  private clusterLayer?: CanvasClusterLayer;
  private imageDimensions = { width: 0, height: 0 };
  private readonly markerRegistry: CanvasMarkerRegistry;
  private clusteringEnabled = true;
  private pendingMapSetup = false;
  private tileSub?: Subscription;
  private viewportSub?: Subscription;
  private replaySub?: Subscription;
  private replayTimestamp$ = new Subject<number>();
  private readonly replayResult$ = new Subject<ReplayTileResult>();
  private isInViewport = false;

  constructor(
    private fileUploadService: FileUploadService,
    private tilesService: TilesService,
    private shadeTools: ShadeToolsService,
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private store: Store<AppState>,
    private elementRef: ElementRef,
    private viewportIntersectionService: ViewportIntersectionService,
    private replayService: CanvasReplayService,
    private featureFlagsService: FeatureFlagsService,
  ) {
    this.markerRegistry = new CanvasMarkerRegistry(this.appRef, this.injector, () => this.clusterLayer);
  }

  private resolveClusteringEnabled(structure: CanvasStructure): boolean {
    return structure.Cluster?.Enabled ??
      this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_CANVAS_CLUSTERING_BY_DEFAULT);
  }

  // Points order differs between fetches but isn't meaningful; sort before comparing
  // to avoid spuriously flagging a change and rebuilding the whole map.
  private pointSortKey(point: CanvasPoint): string {
    return `${CanvasPoint.getId(point)}|${point.TileIDs.join(',')}`;
  }

  private structureSignature(structure: CanvasStructure): string {
    const sortedPoints = [...(structure.Points ?? [])]
      .sort((a, b) => this.pointSortKey(a).localeCompare(this.pointSortKey(b)));
    return JSON.stringify({ ...structure, Points: sortedPoints });
  }

  ngOnInit(): void {
    this.canvasStructure = this.tile.Structure as CanvasStructure;
    this.clusteringEnabled = this.resolveClusteringEnabled(this.canvasStructure);
    this.loadCanvasImage();

    // Setup viewport visibility detection to defer mini-tile polling
    // Use small buffer (0px) to only poll when actually visible - canvas tiles are heavy
    this.viewportSub = this.viewportIntersectionService
      .observe$(this.elementRef.nativeElement, { rootMargin: '0px' })
      .pipe(
        distinctUntilChanged(), // Only emit when visibility actually changes
        tap(isVisible => this.isInViewport = isVisible),
        tap(isVisible => this.updateMiniTilePolling(isVisible))
      )
      .subscribe();

    this.replaySub = this.replayTimestamp$.pipe(
      switchMap(ts => this.replayService.fetchReplayValues(this.getTilesForReplay(), ts))
    ).subscribe(result => this.replayResult$.next(result));

    // Subscribe to store updates for this tile (like regular tiles do)
    if (this.tile?.ID) {
      this.tileSub = this.store.select('tiles').pipe(
        map((state) => state.all[this.tile.ID]),
        filter(t => !!t),
      ).subscribe((tile) => {
        const newTile = Tile.create(tile);
        const oldStructure = this.canvasStructure;
        const newStructure = newTile.Structure as CanvasStructure;

        this.tile = newTile;
        this.canvasStructure = newStructure;
        this.clusteringEnabled = this.resolveClusteringEnabled(newStructure);

        const structureChanged = this.structureSignature(oldStructure) !== this.structureSignature(newStructure);
        if (structureChanged) {
          this.loading.set(true);
          this.loadCanvasImage();

          if (this.map) {
            this.cleanupMap();
            this.pendingMapSetup = true;
          }
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['matchingTileIds'] && !changes['matchingTileIds'].firstChange) {
      this.updateMatchingMarkers();
    }
  }

  ngAfterViewChecked(): void {
    if (this.pendingMapSetup && this.isInViewport && document.getElementById(this.mapId)) {
      this.setupMap();
      this.pendingMapSetup = false;
    }
  }

  ngOnDestroy(): void {
    this.cleanupMap();
    this.tileSub?.unsubscribe();
    this.viewportSub?.unsubscribe();
    this.replaySub?.unsubscribe();
  }

  onMiniTileClick(event: MiniTileClickEvent): void {
    if (event.tiles?.length) {
      this.drawerTiles = event.tiles;
      this.drawerOpen = true;
    }
  }

  closeDrawer(): void {
    this.drawerOpen = false;
  }

  openTileMenu(): void {
    this.tileMenuOpen = true;
  }

  onReplayTimestamp(timestamp: number | null): void {
    if (timestamp === null) {
      this.resumeLiveMode();
    } else {
      this.updateMiniTilesWithTimestamp(timestamp);
    }
  }

  get displayName(): string {
    return this.tile?.Appearance?.DisplayName || this.tile?.Name || 'Canvas Tile';
  }

  get displayIcon(): string {
    return this.tile?.Appearance?.Icon || 'map';
  }

  get headerColor(): string | undefined {
    return this.tile?.Appearance?.Color;
  }

  private loadCanvasImage(): void {
    // If structure exists but has no image, show empty state
    if (this.canvasStructure && !this.canvasStructure.Image) {
      this.loading.set(false);
      return;
    }

    // If no structure at all yet, keep showing spinner
    if (!this.canvasStructure) {
      return;
    }

    this.fileUploadService.fetch(this.canvasStructure.Image).subscribe({
      next: (upload) => {
        if (upload?.URL) {
          this.canvasImageUrl = upload.URL;
          this.loadImageDimensions();
        }
        // Don't set loading=false here - wait for actual image load or error
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  private loadImageDimensions(): void {
    const img = new Image();
    img.onload = () => {
      this.imageDimensions = { width: img.width, height: img.height };
      this.pendingMapSetup = true;
    };
    img.onerror = () => this.loading.set(false);
    img.src = this.canvasImageUrl;
  }

  private setupMap(): void {
    const mapElement = document.getElementById(this.mapId);
    if (!mapElement) return;

    this.cleanupMap();

    const {map, fitToImage} = createCanvasMap(
      this.mapId, this.canvasImageUrl, this.imageDimensions, this.canvasStructure.InitialView,
      { padding: [0, 0], maxZoom: 0 }, () => this.loading.set(false),
    );
    this.map = map;

    this.clusterLayer = new CanvasClusterLayer(
      this.map, CANVAS_MINI_TILE.WIDTH, CANVAS_MAP_CONFIG.maxZoom,
      members => this.onClusterMembersClick(members)
    );
    this.clusterLayer.addTo(this.map);
    this.clusterLayer.setEnabled(this.clusteringEnabled);

    addCanvasMapControls(this.map, {
      onFitBounds: fitToImage,
      clusterToggle: {
        enabled: this.clusteringEnabled,
        onChange: (enabled) => {
          this.clusteringEnabled = enabled;
          this.clusterLayer?.setEnabled(enabled);
        },
      },
    });

    this.updateMatchingMarkers();
  }

  private updateMatchingMarkers(): void {
    if (!this.clusterLayer) return;

    const points = this.canvasStructure.Points ?? [];
    const markerIds = CanvasPoint.getIds(points);

    const desired = points
      .map((point, index) => ({point, markerId: markerIds[index], tileIds: this.filteredTileIdsForPoint(point)}))
      .filter(({tileIds}) => tileIds.length > 0)
      .map(({point, markerId, tileIds}) => ({
        markerId,
        options: {
          position: this.toLatLng(point),
          tileIds,
          shouldPoll: this.isInViewport,
          replayResult$: this.replayResult$.asObservable(),
          onClick: (event: MiniTileClickEvent) => this.onMiniTileClick(event),
        },
      }));

    this.clusterLayer.setMarkers(this.markerRegistry.reconcile(desired));
  }

  private filteredTileIdsForPoint(point: CanvasPoint): TileId[] {
    return filterByMatchingIds(point.TileIDs, id => id, this.matchingTileIds);
  }

  private onClusterMembersClick(members: L.Marker[]): void {
    const allTiles = this.markerRegistry.tilesOf(members);
    if (allTiles.length > 0) {
      this.drawerTiles = allTiles;
      this.drawerOpen = true;
    }
  }

  private toLatLng(point: CanvasPoint): L.LatLngExpression {
    const { width, height } = this.imageDimensions;
    return [point.Y * height, point.X * width];
  }

  private cleanupMap(): void {
    if (!this.map) return;

    const mapRef = this.map;
    this.map = undefined;

    this.markerRegistry.clear();
    this.clusterLayer?.remove();
    this.clusterLayer = undefined;
    destroyCanvasMap(mapRef);
  }

  private editTile(): void {
    if (!this.tile.ID) return;

    this.tilesService.canConfigureTile$(this.tile.ID).subscribe({
      next: canConfigure => {
        if (canConfigure) {
          this.editingTile = true;
        } else {
          this.showPermissionError();
        }
      },
      error: () => this.showPermissionError(),
    });
  }

  private showPermissionError(): void {
    this.shadeTools.errorDialog(`You don't have permissions to configure this tile`);
  }

  private generateUniqueId(): string {
    return Math.random().toString(36).substring(2, 11);
  }

  /**
   * Updates the shouldPoll state for all mini-tile components
   */
  private updateMiniTilePolling(shouldPoll: boolean): void {
    this.markerRegistry.forEach(({componentRef}) => {
      componentRef.instance.shouldPoll = shouldPoll;
    });
  }

  private updateMiniTilesWithTimestamp(timestamp: number): void {
    this.markerRegistry.forEach(({componentRef}) => {
      componentRef.instance.replayTimestamp = timestamp;
    });
    this.replayTimestamp$.next(timestamp);
  }

  private resumeLiveMode(): void {
    this.markerRegistry.forEach(({componentRef}) => {
      componentRef.instance.replayTimestamp = null;
      if (this.isInViewport) {
        componentRef.instance.shouldPoll = true;
      }
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  private getTilesForReplay(): {tileId: TileId; tile: Tile}[] {
    const allTiles = this.store.selectSignal((state: AppState) => state.tiles.all)();
    return (this.canvasStructure?.Points ?? [])
      .flatMap(p => p.TileIDs)
      .filter(id => allTiles[id])
      .map(id => ({tileId: id, tile: Tile.create(allTiles[id])}));
  }
}
